import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
// eslint-disable-next-line import-x/no-empty-named-blocks
import {} from 'analysis-tool/ui/charting/RegressionDataFramesPreview';
import {GetRegressionDataFrames} from 'analysis-tool/ui/charting/regressions/__generated/GetRegressionDataFrames';
import {
  GET_REGRESSION_DATA_FRAMES_QUERY,
  getPartitions,
} from 'analysis-tool/ui/charting/regressions/getPersistedRegressionAnalyses';
import {getRegressionModelVariablesAccessors} from 'analysis-tool/ui/charting/regressions/RegressionModelVariablesAccessors';
import {assert} from 'Asserts';
import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {DataFrame} from 'data-frames/DataFrameTypes';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import HelpButton from 'help/HelpButton';
import DropdownSelector from 'insights/analytics-visualizations/configs/form-components/DropdownSelector';
import {getPipelineStepConfig} from 'pipelines/StepShapeUtils';
import {FONT_PROXIMA_14, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import {ButtonSize} from 'ui-library/buttons/Button';
import {FloatingPlacement, FloatingInteraction} from 'ui-library/floating-ui/FloatingConstants';
import {JSONLeafValue, JSONValue} from 'util/Types';

const PARTITION_SELECTOR_HELP_TEXT =
  "If specified, then only this series' regression model will be applied to the input table. If an input series column is also specified below, then the input table will be filtered by this series value.";

/**
 * Get the selected partition value from the draft step config, if it exists.
 */
function getPartitionValue(config: Record<string, JSONValue>): JSONLeafValue {
  return config.hasOwnProperty('selectedPartitionValue')
    ? (config.selectedPartitionValue as unknown as JSONLeafValue)
    : null;
}

interface RegressionPartitionColumnInformation {
  modelOutputDataFrameId: string;
  modelVariablesDataFrameId: string;
  /** Column def of the column from the regression step's input data frame used as the partition column. */
  partitionColumnDef: DataFrameColumnDef | null;
}

/** Given a regression step, get the output data frame information to query the partition column's values. */
function getRegressionPartitionColumnInformation({
  regressionStep,
}: {
  regressionStep: GetAnalysisPipelineStep;
}): RegressionPartitionColumnInformation {
  const columnName = getPipelineStepConfig(regressionStep, PipelineStepType.REGRESSION, 0).regressionConfig
    .partitionColumn;
  const partitionColumnDef =
    columnName == null
      ? null
      : (regressionStep.inputStepDatas[0]?.dataFrames[0]?.allColumnsJson.find(
          (columnJson) => columnJson.systemName === columnName
        ) ?? null);

  const modelOutputDataFrameId = regressionStep.outputStepDatas.find(
    (stepData) => stepData.name === 'Model output'
  )!.dataFrames[0].id;
  const modelVariablesDataFrameId = regressionStep.outputStepDatas.find(
    (stepData) => stepData.name === 'Model variables'
  )!.dataFrames[0].id;

  return {
    modelOutputDataFrameId,
    modelVariablesDataFrameId,
    partitionColumnDef,
  };
}

export interface PartitionValueSelectorProps {
  config: Record<string, JSONValue>;
  selectedRegressionStep: GetAnalysisPipelineStep;
  setConfig: (newConfig: Record<string, JSONValue>) => void;
}

export default function PartitionValueSelector({
  config,
  selectedRegressionStep,
  setConfig,
}: PartitionValueSelectorProps): JSX.Element | null {
  const {modelOutputDataFrameId, modelVariablesDataFrameId, partitionColumnDef} =
    getRegressionPartitionColumnInformation({regressionStep: selectedRegressionStep});

  const outputDataFramesQueryResult = useBenchlingQuery<GetRegressionDataFrames>(
    GET_REGRESSION_DATA_FRAMES_QUERY,
    {variables: {modelOutputDataFrameId, modelVariablesDataFrameId}}
  );

  const partitionValue = getPartitionValue(config);
  function setPartitionValue(newPartitionValue: JSONLeafValue): void {
    setConfig({...config, selectedPartitionValue: newPartitionValue});
  }

  if (partitionColumnDef == null || outputDataFramesQueryResult.type !== QueryResultType.SUCCESS) {
    return null;
  }

  const {modelVariablesDataFrame} = outputDataFramesQueryResult.data;
  assert(!isInaccessible(modelVariablesDataFrame), 'User does not have access to variables data frame');

  const variablesDataFrame = modelVariablesDataFrame.dataFrameJson as DataFrame;
  const accessors = getRegressionModelVariablesAccessors(variablesDataFrame);
  const partitions = getPartitions(variablesDataFrame, accessors.partitionAccessor ?? null);

  return (
    <div className={css(styles.section)}>
      <div className={css(styles.variableHeader)}>
        <span className={css(styles.variableText)}>Select series value (optional)</span>
        <HelpButton
          renderContent={() => (
            <span className="u-pre-wrap u-normal-font-weight">{PARTITION_SELECTOR_HELP_TEXT}</span>
          )}
          iconClassName="u-small-spacing-left"
          placement={FloatingPlacement.TOP}
          interaction={FloatingInteraction.HOVER}
        />
      </div>
      <DropdownSelector
        buttonSize={ButtonSize.MEDIUM}
        allowNull={true}
        onChange={setPartitionValue}
        searchable={true}
        selectedValue={partitionValue}
        optionDatas={partitions.map((partition) => ({name: `${partition}`, value: partition}))}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  section: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 8,
  },
  variableHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  variableText: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
});
